import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { get, isEmpty } from 'lodash';

import { Text } from '~/components/core';
import ArrayMultiselectFieldWithChipsFormik from '~/components/core/ArrayMultiselectFieldWithChipsFormik';
import Grid from '~/components/core/Atomic/Grid/Grid';

import CardDialog from '../../../../CardDialog';
import { buildCategoryLimitOptions, usePolicyCategoryOptions } from '../PolicyCategoriesOptions';

import ArrayFieldFormik from './NestedFieldFormik/ArrayFieldFormik';
import SwitchOrDelete from './SwitchOrDelete';

const OptionCategoryField = ({ id, label, disabled, onDelete: _onDelete, isExistingValue: _isExistingValue }) => {
  const { values } = useFormikContext();
  const policyCategoryOptions = usePolicyCategoryOptions();
  const incidentCategories = get(values, 'configuration.categories', [])?.map((category) => ({
    id: category.id,
    value: category.id,
    label: category.desc,
  }));
  // Same grouped list as the field-level "Limit to Categories" — option-level limits share
  // the flat key namespace.
  const categories = buildCategoryLimitOptions(incidentCategories, policyCategoryOptions);

  return (
    <ArrayMultiselectFieldWithChipsFormik
      id={id}
      label={label}
      options={categories}
      disabled={disabled || isEmpty(categories)}
    />
  );
};

OptionCategoryField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  onDelete: PropTypes.func,
  isExistingValue: PropTypes.bool,
};

const NESTED_FIELD_CONFIG = [
  { id: 'desc', label: 'Display name', type: 'text' },
  { id: 'id', label: 'Key', type: 'text', disableEditAfterInitialSet: true },
  {
    id: 'incident_sub_type_categories',
    label: 'Limit to Categories',
    type: 'custom',
    customComponent: OptionCategoryField,
  },
  {
    id: 'active',
    label: '',
    type: 'custom',
    customComponent: SwitchOrDelete,
  },
];
const INNER_FIELD_INITIAL_VALUES = { id: '', desc: '', active: true, incident_sub_type_categories: null };

const OptionsCard = ({
  idPrefix,
  fieldId,
  disableAdd,
  originalFieldId,
  hideCategories = false,
  withDndReorder = false,
  readOnly = false,
}) => {
  const { values, isSubmitting, setFieldValue } = useFormikContext();
  const id = idPrefix ? `${idPrefix}.${fieldId}` : fieldId;
  const type = get(values, `${id}.type`, '');
  const isOptionsField = ['select', 'multiselect'].includes(type);
  const title = <Text variant={Text.VARIANTS.SM}>{`${type === 'select' ? 'Single' : 'Multi'} Select Options`}</Text>;

  const nestedFieldConfig = hideCategories
    ? NESTED_FIELD_CONFIG.filter((field) => field.id !== 'incident_sub_type_categories')
    : NESTED_FIELD_CONFIG;

  // Set default value for options active to true - only on first render. One array-level update,
  // skipped when there is nothing to backfill: a per-option setFieldValue re-validates and
  // re-renders the whole FNOL form once per option, which hung the browser on a large dropdown.
  // A read-only row is never backfilled: it goes back in the payload exactly as it was loaded.
  useEffect(() => {
    if (readOnly) {
      return;
    }
    const options = get(values, `${id}.options`, []);
    if (isEmpty(options)) {
      setFieldValue(`${id}.options`, [INNER_FIELD_INITIAL_VALUES]);
      return;
    }
    if (options.every((option) => typeof option?.active === 'boolean')) {
      return;
    }
    setFieldValue(
      `${id}.options`,
      options.map((option) => (typeof option?.active === 'boolean' ? option : { ...option, active: true }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isOptionsField) {
    return <></>;
  }

  return (
    <Grid item xs={12}>
      <CardDialog noCardTitle contentStyle={{ padding: '16px' }}>
        <ArrayFieldFormik
          fieldId={`${id}.options`}
          label={title}
          innerObjectConfig={nestedFieldConfig}
          disabled={isSubmitting || readOnly}
          initialNewItemValues={INNER_FIELD_INITIAL_VALUES}
          disableAddBtn={disableAdd}
          originalFieldId={originalFieldId ? `${originalFieldId}.options` : null}
          withDndReorder={withDndReorder}
        />
      </CardDialog>
    </Grid>
  );
};

OptionsCard.propTypes = {
  idPrefix: PropTypes.string,
  fieldId: PropTypes.string.isRequired,
  disableAdd: PropTypes.bool,
  originalFieldId: PropTypes.string,
  hideCategories: PropTypes.bool,
  withDndReorder: PropTypes.bool,
  readOnly: PropTypes.bool,
};

export default OptionsCard;
